import React, { useState, useEffect, useRef } from 'react';
import { Modal, DatePicker, TimePicker, Checkbox } from 'antd';
import { CriticalIncidentIcon, CalendarDarkIcon, ClockDarkIcon } from 'components/Elements/CustomSVGIcon';
import dayjs, { Dayjs } from 'dayjs';
import type { UploadFile } from 'antd/lib/upload/interface';
import FileUploader from 'components/Form/FileUploader';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';
import { DiaryEntry, IncidentEmailPreview } from 'core/incidents/types';
import EmailPreviewModal from '../EmailPreviewModal';
import './addDiaryEntryModal.scss';

const MAX_TEXT_LENGTH = MAX_LONG_TEXT_LENGTH;

export interface DiaryEntryFormValues {
    text: string;
    files: UploadFile[];
    entryOccurredAt: string | null;
    sentToDistribution: boolean;
}

interface AddDiaryEntryModalProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: DiaryEntryFormValues) => Promise<void> | void;
    isCritical: boolean;
    // When provided, the modal edits this existing entry (correct typos /
    // amend details) instead of creating a new one.
    // eslint-disable-next-line react/require-default-props
    initialEntry?: DiaryEntry | null;
    // Fetches a preview of the distribution email for the current form values.
    // When provided, a "Preview email" button is shown while "send to
    // distribution" is ticked. Returns null if the preview could not be built.
    // eslint-disable-next-line react/require-default-props
    onPreview?: (values: DiaryEntryFormValues) => Promise<IncidentEmailPreview | null>;
}

const AddDiaryEntryModal: React.FC<AddDiaryEntryModalProps> = ({
    visible,
    onClose,
    onSubmit,
    isCritical,
    initialEntry = null,
    onPreview,
}) => {
    const isEditMode = !!initialEntry;
    const [text, setText] = useState('');
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [date, setDate] = useState<Dayjs | null>(dayjs());
    const [time, setTime] = useState<Dayjs | null>(dayjs());
    const [sentToDistribution, setSentToDistribution] = useState(false);
    const [textError, setTextError] = useState('');
    const [dateTimeError, setDateTimeError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewData, setPreviewData] = useState<IncidentEmailPreview | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    // Synchronous lock: setIsPreviewing(true) is async, so a rapid double-click
    // in the same tick could fire two preview requests before state updates.
    const previewInFlightRef = useRef(false);

    useEffect(() => {
        if (visible) {
            // In edit mode, seed the form from the existing entry; otherwise
            // start blank. Attachments are never pre-loaded — editing leaves
            // existing media untouched.
            const seededOccurredAt = initialEntry?.entryOccurredAt ? dayjs(initialEntry.entryOccurredAt) : dayjs();
            setText(initialEntry?.text ?? '');
            setFileList([]);
            setDate(seededOccurredAt);
            setTime(seededOccurredAt);
            setSentToDistribution(initialEntry?.sentToDistribution ?? false);
            setTextError('');
            setDateTimeError('');
            setIsSubmitting(false);
            setPreviewVisible(false);
            setPreviewData(null);
            setIsPreviewing(false);
            previewInFlightRef.current = false;
        }
    }, [visible, initialEntry]);

    // Validate the form and build the submit values, or return null (setting the
    // relevant error) when invalid. Shared by submit and preview so the two can't
    // validate differently.
    const buildValidatedValues = (): DiaryEntryFormValues | null => {
        if (!text.trim()) {
            setTextError('Diary entry text is required.');
            return null;
        }
        if (text.length > MAX_TEXT_LENGTH) {
            setTextError(`Diary entry must not exceed ${MAX_TEXT_LENGTH} characters.`);
            return null;
        }
        setTextError('');

        let entryOccurredAt: string | null = null;
        if (isCritical) {
            if (!date || !time) {
                setDateTimeError('Date and time are required.');
                return null;
            }
            const combined = date
                .hour(time.hour())
                .minute(time.minute())
                .second(time.second());
            if (combined.isAfter(dayjs())) {
                setDateTimeError('Date and time cannot be in the future.');
                return null;
            }
            setDateTimeError('');
            entryOccurredAt = combined.toISOString();
        }

        return { text, files: fileList, entryOccurredAt, sentToDistribution };
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        const values = buildValidatedValues();
        if (!values) return;

        setIsSubmitting(true);
        try {
            await Promise.resolve(onSubmit(values));
            // Parent owns modal closing via setIsDiaryModalOpen
        } catch {
            // Submit failed — modal stays open so the user can retry
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePreview = async () => {
        // Ref guard is synchronous, so it blocks a second click in the same tick
        // that the isPreviewing state update hasn't reflected yet.
        if (previewInFlightRef.current || !onPreview) return;
        const values = buildValidatedValues();
        if (!values) return;

        previewInFlightRef.current = true;
        setIsPreviewing(true);
        setPreviewData(null);
        setPreviewVisible(true);
        try {
            const result = await onPreview(values);
            if (result) {
                setPreviewData(result);
            } else {
                // Preview failed (error already surfaced by the thunk) — close it.
                setPreviewVisible(false);
            }
        } catch {
            setPreviewVisible(false);
        } finally {
            previewInFlightRef.current = false;
            setIsPreviewing(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={560}
            className="add-diary-entry-modal"
            centered>
            <div className="add-diary-entry-container">
                <h2 className="heading">{isEditMode ? 'Edit Diary Entry' : 'Add Diary Entry'}</h2>

                {isCritical && (
                    <div className="critical-alert">
                        <CriticalIncidentIcon />
                        <span className="critical-alert-text">Critical Incident</span>
                    </div>
                )}

                {isCritical && (
                    <div className="date-time-row">
                        <div className="date-time-field">
                            <h3>Date Entry Occurred</h3>
                            <DatePicker
                                value={date}
                                onChange={(val) => { setDate(val); setDateTimeError(''); }}
                                disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
                                format="DD/MM/YYYY"
                                suffixIcon={<CalendarDarkIcon w={18} h={20} />}
                                className="date-picker" />
                        </div>
                        <div className="date-time-field">
                            <h3>Time Entry Occurred</h3>
                            <TimePicker
                                value={time}
                                onChange={(val) => { setTime(val); setDateTimeError(''); }}
                                format="HH:mm"
                                suffixIcon={<ClockDarkIcon w={18} h={18} />}
                                className="time-picker"
                                disabledTime={() => {
                                    if (!date || !date.isSame(dayjs(), 'day')) return {};
                                    const now = dayjs();
                                    return {
                                        disabledHours: () => Array.from(
                                            { length: 24 },
                                            (_, h) => h,
                                        ).filter((h) => h > now.hour()),
                                        disabledMinutes: (h: number) => (
                                            h === now.hour()
                                                ? Array.from({ length: 60 }, (_, m) => m).filter((m) => m > now.minute())
                                                : []
                                        ),
                                    };
                                }} />
                        </div>
                        {dateTimeError && <span className="field-error date-time-error">{dateTimeError}</span>}
                    </div>
                )}

                <div className="details-field">
                    <h3>Incident Details</h3>
                    <textarea
                        className={`details-textarea${textError ? ' input-error' : ''}`}
                        value={text}
                        maxLength={MAX_TEXT_LENGTH}
                        onChange={(e) => { setText(e.target.value); setTextError(''); }} />
                    {textError && <span className="field-error">{textError}</span>}
                </div>

                {/* Attachments and distribution apply only when creating an
                    entry; editing corrects the wording/details of the existing
                    entry and never re-sends a distribution report. */}
                {!isEditMode && (
                    <>
                        <div className="supporting-docs-field">
                            <h3>Supporting Documentation</h3>
                            <FileUploader
                                fileList={fileList}
                                onChange={setFileList}
                                onRemove={(uid) => setFileList((prev) => prev.filter((f) => f.uid !== uid))}
                                showFileList />
                        </div>

                        <div className="distribution-checkbox">
                            <Checkbox
                                checked={sentToDistribution}
                                onChange={(e) => setSentToDistribution(e.target.checked)}>
                                Send report to distribution centre
                            </Checkbox>
                        </div>
                    </>
                )}

                <div className="button-wrapper">
                    <button type="button" className="confirm-button" onClick={handleSubmit} disabled={isSubmitting}>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {isSubmitting ? (isEditMode ? 'Saving...' : 'Submitting...') : (isEditMode ? 'Save' : 'Submit')}
                    </button>
                    {/* Preview the distribution email before sending it out to the
                        whole list — only meaningful when a report will actually
                        be sent. */}
                    {!isEditMode && sentToDistribution && onPreview && (
                        <button type="button" className="preview-button" onClick={handlePreview} disabled={isPreviewing}>
                            Preview email
                        </button>
                    )}
                    <button type="button" className="cancel-button" onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>

            <EmailPreviewModal
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                isLoading={isPreviewing}
                preview={previewData} />
        </Modal>
    );
};

export default AddDiaryEntryModal;
